"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { setUserLocale } from "@/services/locale";
import { ChevronDown, Mail, Moon, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useState, useTransition } from "react";
import { Button } from "../ui/button";

export default function AppFooter({
  support_email,
}: {
  support_email: string;
}) {
  const t = useTranslations("Footer");
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [language, setLanguage] = useState("FR");
  const { setTheme, theme } = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPending, startTransition] = useTransition();

  const handleLanguageChange = (lang: string) => {
    // Ensure lang is a valid locale
    const locale = lang.toLowerCase() as "fr" | "de" | "en";
    startTransition(() => {
      setUserLocale(locale);
    });
    setLanguage(lang);
    setIsLanguageOpen(false);
  };

  return (
    <header className="w-full bg-background border-t py-4 px-6">
      <div className="flex justify-between items-center">
        {/* Logo and Title */}
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-bold text-lg text-foreground">
              {t("app_name")}{" "}
              <small className="text-xs text-muted-foreground">
                v{process.env.PUBLIC_PACKAGE_VERSION}
              </small>
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("association_name")}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-6">
          {/* Support Link */}
          {support_email && (
            <Link
              href={`mailto:${support_email}`}
              className="text-sm text-foreground flex items-center hover:text-foreground/80"
            >
              <span>{t("support")}</span>
              <button className="p-1">
                <Mail className="w-5 h-5" />
              </button>
            </Link>
          )}

          {/* Light/Dark Toggle */}
          <span className="text-foreground">{t("lightmode")}</span>
          <div className="relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">{t("lightmode")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background border">
                <DropdownMenuItem
                  onClick={() => setTheme("light")}
                  className={`${
                    theme === "light" ? "bg-accent" : ""
                  } text-foreground`}
                >
                  {t("light")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme("dark")}
                  className={`${
                    theme === "dark" ? "bg-accent" : ""
                  } text-foreground`}
                >
                  {t("dark")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme("system")}
                  className={`${
                    theme === "system" ? "bg-accent" : ""
                  } text-foreground`}
                >
                  {t("system")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Language Dropdown */}
          <div className="relative">
            <div className="flex items-center gap-1 text-sm">
              <span className="text-foreground">{t("language")}</span>
              <DropdownMenu
                open={isLanguageOpen}
                onOpenChange={setIsLanguageOpen}
              >
                <DropdownMenuTrigger className="flex items-center gap-1 px-2 py-1 rounded hover:bg-accent text-foreground">
                  <span className="font-medium">{language}</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-16 p-0 bg-background border"
                >
                  <DropdownMenuItem
                    className="justify-center py-2 hover:bg-accent text-foreground"
                    onClick={() => handleLanguageChange("FR")}
                  >
                    {t("FR")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="justify-center py-2 hover:bg-accent text-foreground"
                    onClick={() => handleLanguageChange("DE")}
                  >
                    {t("DE")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="justify-center py-2 hover:bg-accent text-foreground"
                    onClick={() => handleLanguageChange("EN")}
                  >
                    {t("EN")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
